/**
 * TEMP-H-005: Enhanced Error Boundary
 * 
 * React error boundary with graceful degradation for type validation failures.
 * Provides user-friendly error messaging and retry capabilities.
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Bug, Network } from 'lucide-react';
import { ApiValidationError, ApiNetworkError } from '@/lib/validated-api-client';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  context?: string;
  showDetails?: boolean;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
}

/**
 * Enhanced error boundary with type-aware error handling
 */
export class TypeSafeErrorBoundary extends Component<Props, State> {
  private maxRetries = 3;
  
  constructor(props: Props) {
    super(props);
    
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    };
  }
  
  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error
    };
  }
  
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    
    // Log error with context
    console.error(`[ErrorBoundary${this.props.context ? ` - ${this.props.context}` : ''}]:`, error);
    console.error('Component stack:', errorInfo.componentStack);
    
    // Call optional error handler
    this.props.onError?.(error, errorInfo);
  }
  
  handleRetry = () => {
    if (this.state.retryCount < this.maxRetries) {
      this.setState(prevState => ({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: prevState.retryCount + 1
      }));
    }
  };
  
  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    });
  };
  
  getErrorType(): 'validation' | 'network' | 'component' | 'unknown' {
    const { error } = this.state;
    
    if (error instanceof ApiValidationError) {
      return 'validation';
    } else if (error instanceof ApiNetworkError) {
      return 'network';
    } else if (error?.name === 'ChunkLoadError' || error?.message?.includes('Loading chunk')) {
      return 'network';
    } else if (error?.stack?.includes('at Object.')) {
      return 'component';
    }
    
    return 'unknown';
  }
  
  getErrorIcon() {
    const errorType = this.getErrorType();
    const iconClass = "w-6 h-6";
    
    switch (errorType) {
      case 'validation':
        return <Bug className={`${iconClass} text-amber-400`} />;
      case 'network':
        return <Network className={`${iconClass} text-red-400`} />;
      case 'component':
        return <AlertTriangle className={`${iconClass} text-orange-400`} />;
      default:
        return <AlertTriangle className={`${iconClass} text-red-400`} />;
    }
  }
  
  getErrorTitle(): string {
    const errorType = this.getErrorType();
    
    switch (errorType) {
      case 'validation':
        return 'Data Format Error';
      case 'network':
        return 'Connection Error';  
      case 'component':
        return 'Component Error';
      default:
        return 'Unexpected Error';
    }
  }
  
  getErrorMessage(): string {
    const { error } = this.state;
    const context = this.props.context || 'this section';
    
    if (error instanceof ApiValidationError || error instanceof ApiNetworkError) {
      return error.getUserMessage();
    }
    
    const errorType = this.getErrorType();
    
    switch (errorType) {
      case 'validation':
        return `Received unexpected data format for ${context}. This might be due to a recent update.`;
      case 'network':
        return `Failed to load ${context}. Please check your internet connection and try again.`;
      case 'component':
        return `A problem occurred while displaying ${context}. This is likely a temporary issue.`;
      default:
        return `Something went wrong with ${context}. Please try refreshing the page.`;
    }
  }
  
  canRetry(): boolean {
    const { error, retryCount } = this.state;
    
    if (retryCount >= this.maxRetries) {
      return false;
    }
    
    // Check if this type of error should be retried
    return (
      error instanceof ApiNetworkError && error.shouldRetry()
    ) || (
      this.getErrorType() === 'network'
    ) || (
      this.getErrorType() === 'validation'
    );
  }
  
  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      const errorTitle = this.getErrorTitle();
      const errorMessage = this.getErrorMessage();
      const canRetry = this.canRetry();
      const showDetails = this.props.showDetails && process.env.NODE_ENV === 'development';
      
      return (
        <div className="min-h-[200px] flex items-center justify-center p-6">
          <div className="max-w-md w-full">
            <div className="bg-gray-900/50 rounded-lg border border-red-500/20 p-6">
              {/* Error Icon and Title */}
              <div className="flex items-start gap-3 mb-4">
                {this.getErrorIcon()}
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {errorTitle}
                  </h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {errorMessage}
                  </p>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center gap-3">
                {canRetry && (
                  <button
                    onClick={this.handleRetry}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Try Again
                    {this.state.retryCount > 0 && (
                      <span className="text-blue-200">
                        ({this.state.retryCount}/{this.maxRetries})
                      </span>
                    )}
                  </button>
                )}
                
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Refresh Page
                </button>
              </div>
              
              {/* Development Details */}
              {showDetails && this.state.error && (
                <details className="mt-4 p-3 bg-gray-800 rounded text-xs">
                  <summary className="cursor-pointer text-gray-400 mb-2">
                    Error Details (Development)
                  </summary>
                  <div className="text-red-300 font-mono whitespace-pre-wrap">
                    {this.state.error.toString()}
                    {this.state.errorInfo && (
                      <>
                        {'\n\nComponent Stack:'}
                        {this.state.errorInfo.componentStack}
                      </>
                    )}
                  </div>
                </details>
              )}
            </div>
          </div>
        </div>
      );
    }
    
    return this.props.children;
  }
}

/**
 * Simplified error boundary for specific contexts
 */
export function ApiErrorBoundary({ 
  children, 
  context, 
  fallback 
}: { 
  children: ReactNode; 
  context: string;
  fallback?: ReactNode;
}) {
  return (
    <TypeSafeErrorBoundary 
      context={context}
      fallback={fallback}
      showDetails={true}
    >
      {children}
    </TypeSafeErrorBoundary>
  );
}

/**
 * Error boundary specifically for settings components
 */
export function SettingsErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ApiErrorBoundary context="settings" fallback={
      <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-400 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>Unable to load settings. Please refresh to try again.</span>
        </div>
      </div>
    }>
      {children}
    </ApiErrorBoundary>
  );
}

/**
 * Error boundary for timeline/events components  
 */
export function TimelineErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ApiErrorBoundary context="timeline" fallback={
      <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4">
        <div className="flex items-center gap-2 text-orange-400 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>Timeline temporarily unavailable. Some events may not display correctly.</span>
        </div>
      </div>
    }>
      {children}
    </ApiErrorBoundary>
  );
}